import React from "react"

import {usePageSize} from "../../components/RowCountPicker.tsx"
import {TOrderBy} from "../../components/Table/utils/shared.ts"
import {
  AKlaraDashboardDealChipValues,
  AKlaraDashboardDealViewValues,
  ASalesAssistantDealPriorities,
} from "../../services/types.generated.ts"
import {createSimpleContext} from "../../utils/context.tsx"
import {useSalesAssistantSalesPersonFilter} from "../SalesAssistant/hooks.ts"
import {useKlaraPeriod} from "./useKlaraPeriod.ts"

export type TKlaraView = AKlaraDashboardDealViewValues
export type TKlaraChip = AKlaraDashboardDealChipValues
export type TKlaraLeadsColumn = "lead" | "sales_person" | "priority" | "stage" | "status" | "next_activity" | "actions"
/** The leads table's sorted column and direction; undefined keeps the backend's default order. */
export type TKlaraLeadsOrder = TOrderBy<TKlaraLeadsColumn>
export type TKlaraDrawer = {kind: "updates"; entryId?: number} | {kind: "lead"; dealId: number} | null

export const KLARA_DEFAULT_PAGE_SIZE = 10
/** Remembers the leads table's rows per page, shared with its row count picker. */
export const KLARA_LEADS_PAGE_SIZE_KEY = "klara dashboard leads"

export type TKlaraFilters = {
  view: TKlaraView
  chip: TKlaraChip | null
  priorities: ASalesAssistantDealPriorities[]
  /** The search requests use; inputs debounce into it, so committing it and the page reset happen together. */
  search: string
  order: TKlaraLeadsOrder
  /** Increments whenever the filters are cleared, so the search input drops a draft it has not committed yet. */
  searchResetVersion: number
  page: number
  pageSize: number
  drawer: TKlaraDrawer
  salesPersonExternalId: string | undefined
  /** Increments whenever the period or the sales person changes, so open drawers can reset and refetch. */
  scopeVersion: number
  setView: (view: TKlaraView) => void
  setChip: (chip: TKlaraChip | null) => void
  setPriorities: (priorities: ASalesAssistantDealPriorities[]) => void
  setSearch: (search: string) => void
  setOrder: (order: TKlaraLeadsOrder) => void
  setPage: (page: number) => void
  setSalesPersonExternalId: (salesPersonExternalId: string | undefined) => void
  openDrawer: (drawer: NonNullable<TKlaraDrawer>) => void
  closeDrawer: () => void
  clearFilters: () => void
  clearForTeamDrillDown: () => void
}

export const KlaraFiltersContext = createSimpleContext<TKlaraFilters>("klara filters")

type TLocalState = Pick<
  TKlaraFilters,
  "view" | "chip" | "priorities" | "search" | "order" | "searchResetVersion" | "page" | "drawer"
>

const initialState: TLocalState = {
  view: AKlaraDashboardDealViewValues.Attention,
  chip: null,
  priorities: [],
  search: "",
  order: undefined,
  searchResetVersion: 0,
  page: 1,
  drawer: null,
}

export const KlaraFiltersProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const [period] = useKlaraPeriod()
  const [salesPersonExternalId, setSalesPersonExternalId] = useSalesAssistantSalesPersonFilter()
  const pageSize = usePageSize(KLARA_LEADS_PAGE_SIZE_KEY, KLARA_DEFAULT_PAGE_SIZE)
  const [state, setState] = React.useState<TLocalState>(initialState)

  const scope = JSON.stringify([period, salesPersonExternalId ?? null])
  const [scopeState, setScopeState] = React.useState({scope, version: 0})
  const scopeVersion = scopeState.version

  // Adjusting state during render (instead of in an effect) keeps the stale page from ever reaching a request.
  if (scope !== scopeState.scope) {
    setScopeState({scope, version: scopeState.version + 1})
    setState(current => ({...current, page: 1}))
  }

  // The page size is stored outside this state (the row count picker writes it), so a change is noticed here.
  const [seenPageSize, setSeenPageSize] = React.useState(pageSize)
  if (pageSize !== seenPageSize) {
    setSeenPageSize(pageSize)
    setState(current => ({...current, page: 1}))
  }

  const actions = React.useMemo(() => {
    const update = (patch: Partial<TLocalState>) => setState(current => ({...current, ...patch}))
    const clearFilters = () => {
      setState(current => ({
        ...current,
        chip: null,
        priorities: [],
        search: "",
        searchResetVersion: current.searchResetVersion + 1,
        page: 1,
      }))
      setSalesPersonExternalId(undefined)
    }

    return {
      setView: (view: TKlaraView) => update({view, chip: null, page: 1}),
      setChip: (chip: TKlaraChip | null) => update({chip, page: 1}),
      setPriorities: (priorities: ASalesAssistantDealPriorities[]) => update({priorities, page: 1}),
      setSearch: (search: string) => update({search, page: 1}),
      setOrder: (order: TKlaraLeadsOrder) => update({order, page: 1}),
      setPage: (page: number) => update({page}),
      setSalesPersonExternalId,
      openDrawer: (drawer: NonNullable<TKlaraDrawer>) => update({drawer}),
      closeDrawer: () => update({drawer: null}),
      clearFilters,
      clearForTeamDrillDown: clearFilters,
    }
  }, [setSalesPersonExternalId])

  const value = React.useMemo<TKlaraFilters>(
    () => ({...state, ...actions, pageSize, salesPersonExternalId, scopeVersion}),
    [state, actions, pageSize, salesPersonExternalId, scopeVersion]
  )

  return <KlaraFiltersContext value={value}>{children}</KlaraFiltersContext>
}
